/* Next-session targets: load rounding, the learned increment, program
 * templates and blocks (switching bench programs), double / linear
 * progression and the target precedence for one exercise on one date. Pure
 * functions; the Python twin is src/fitlogic/progression.py and both are
 * pinned by vectors/progression.json.
 *
 * Precedence (docs/fitness-data-model.md): prescriptions[date][exercise],
 * then the active programs block, then the plan exercise's progression rule,
 * then last session's numbers. Only dates before the asked date feed a
 * target, so ticking today's sets never moves today's target.
 *
 * `pct` is a percent of the training max (85 = 85%); values up to 1.5 are
 * read as fractions, because Claude sometimes writes 0.85. A program set may
 * carry `add` (kg on top of the pct load; Smolov Jr weeks 2 and 3). A block
 * may carry `planDayIds` to apply only on those plan days (bench 5/3/1 on
 * Push A while Push B keeps its own 3x10); without it, every day. */

import templatesData from './programTemplates.json';
import { deloadOn, deloadSets, trainingLogs } from './deload';
import { completedSets, epley, isAssisted } from './records';
import { planDayFor } from './schedule';

const DATE_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const RULES = ['double', 'linear', 'manual'];
const DEFAULT_STEP = 2.5;
// A jump between sessions this big is a gym change or a comeback, not a step.
const JUMP_KG = 10;
const JUMP_PCT = 0.25;
const UNUSUAL_PCT = 0.8;
const BACK_OFF_PCT = 0.1;
const FAILS_BEFORE_BACK_OFF = 3;
const E1RM_DAYS = 60;
const LOWER_BODY_RE = /squat|deadlift|\bdl\b|leg|lunge|hip thrust|rdl|romanian|good morning|hack|calf|glute/i;

function deepFreeze(v) {
  if (v && typeof v === 'object') {
    Object.values(v).forEach(deepFreeze);
    Object.freeze(v);
  }
  return v;
}

/**
 * Program templates from programTemplates.json (frozen; materializeProgram
 * hands out copies). Each: { id, name, description, usesTrainingMax, tmPct
 * (% of e1RM the training max should be: 90, Smolov Jr 100), defaultTmIncrement
 * (upper body; lower body gets double), rule ('linear' | 'double' for the
 * history-driven templates, else null), weeks: [{ label, sets: [{ pct?, add?,
 * reps, repMin?, amrap? }] }] }.
 */
export const PROGRAM_TEMPLATES = deepFreeze(templatesData.templates);

function isNum(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

function isObj(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function posInt(v) {
  return isNum(v) && v >= 1 ? Math.floor(v) : null;
}

/* Half-up like the Python twin's floor(x * f + 0.5) / f; `|| 0` drops -0. */
function roundF(x, f) {
  return Math.round(x * f) / f || 0;
}
function round2(x) { return roundF(x, 100); }

/* Numbers in sentences: JS prints 80 and 82.5; the twin's _fmt matches. */
function fmt(x) {
  return String(x);
}

function dayNumber(key) {
  if (typeof key !== 'string' || !DATE_RE.test(key)) return null;
  const [y, m, d] = key.split('-').map(Number);
  const back = new Date(Date.UTC(y, m - 1, d));
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
  return Math.round(back.getTime() / 86400000);
}

function keyOf(n) {
  return new Date(n * 86400000).toISOString().slice(0, 10);
}

/* Monday = 0; day 0 (1970-01-01) was a Thursday. */
function monIndex(n) {
  return (((n + 3) % 7) + 7) % 7;
}

function dateKeys(logs) {
  if (!isObj(logs)) return [];
  return Object.keys(logs).filter(k => dayNumber(k) !== null).sort();
}

function setsOf(logs, date, name) {
  const day = isObj(logs) ? logs[date] : undefined;
  return isObj(day) ? day[name] : undefined;
}

function hasDoneSet(sets) {
  return Array.isArray(sets) && sets.some(s => isObj(s) && s.done);
}

function median(values) {
  const v = [...values].sort((a, b) => a - b);
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

function stepOf(v) {
  return isNum(v) && v > 0 ? v : DEFAULT_STEP;
}

function templateById(id) {
  return PROGRAM_TEMPLATES.find(t => t.id === id) || null;
}

function resolveAssisted(name, opts) {
  if (opts && typeof opts.assisted === 'boolean') return opts.assisted;
  return isAssisted(name, opts && opts.exerciseMeta);
}

function topWeight(sets, assisted) {
  let top = null;
  for (const s of sets) {
    if (top === null || (assisted ? s.weight < top : s.weight > top)) top = s.weight;
  }
  return top;
}

/* Epley without the 12-rep cap: only used as a ratio between sessions. */
function rawE1rm(sets) {
  let best = null;
  for (const s of sets) {
    const e = s.reps === 1 ? s.weight : s.weight * (1 + s.reps / 30);
    if (best === null || e > best) best = e;
  }
  return best;
}

/**
 * Round a load to the plate / stack step.
 * @param {number} weight kg
 * @param {number} [roundTo=2.5] step in kg; a non-number means 2.5, 0 or less
 *   rounds to 0.01 only
 * @param {'nearest'|'down'} [mode='nearest'] nearest is half-up
 * @returns {number|null} null when weight is not a number
 */
export function roundLoad(weight, roundTo = DEFAULT_STEP, mode = 'nearest') {
  if (!isNum(weight)) return null;
  const step = isNum(roundTo) ? roundTo : DEFAULT_STEP;
  if (step <= 0) return round2(weight);
  const q = weight / step;
  // The epsilon keeps 89.99999999999999 / 2.5 from flooring a whole step down.
  const n = mode === 'down' ? Math.floor(q + 1e-9) : Math.round(q);
  return round2(n * step);
}

/**
 * The load step this exercise actually moves in: the most common positive
 * change between consecutive sessions' top weights (assisted: the most
 * common drop in counterweight). Jumps over 10 kg that are also over 25% of
 * the lighter weight are gym changes or comebacks and are ignored.
 * @param {object} logs state.logs
 * @param {string} name exercise name
 * @param {number} [fallback=2.5] returned without a usable step (0 allowed)
 * @param {{assisted?: boolean, exerciseMeta?: object}} [opts]
 * @returns {number} kg, 0.01 precision; ties go to the smaller step
 */
export function learnIncrement(logs, name, fallback = DEFAULT_STEP, opts = {}) {
  const fb = isNum(fallback) && fallback >= 0 ? fallback : DEFAULT_STEP;
  const assisted = resolveAssisted(name, opts);
  const counts = new Map();
  let prev = null;
  for (const date of dateKeys(logs)) {
    const sets = completedSets(setsOf(logs, date, name));
    if (!sets.length) continue;
    const top = topWeight(sets, assisted);
    if (prev !== null) {
      const step = round2(assisted ? prev - top : top - prev);
      const jump = step > JUMP_KG && step > Math.min(prev, top) * JUMP_PCT;
      if (step > 0 && !jump) counts.set(step, (counts.get(step) || 0) + 1);
    }
    prev = top;
  }
  let best = null;
  let bestN = 0;
  counts.forEach((n, step) => {
    if (n > bestN || (n === bestN && step < best)) { best = step; bestN = n; }
  });
  return best === null ? fb : best;
}

function cleanSet(s) {
  const out = {};
  if (isNum(s.pct) && s.pct > 0) out.pct = s.pct;
  if (isNum(s.add) && s.add !== 0) out.add = s.add;
  if (isNum(s.weight) && s.weight >= 0) out.weight = s.weight;
  out.reps = isNum(s.reps) && s.reps >= 0 ? s.reps : null;
  if (isNum(s.repMin) && s.repMin >= 0) out.repMin = s.repMin;
  if (s.amrap === true) out.amrap = true;
  if (isNum(s.rir)) out.rir = s.rir;
  return out;
}

function cleanWeeks(weeks) {
  return weeks.filter(isObj).map((w, i) => ({
    label: typeof w.label === 'string' && w.label.trim() ? w.label.trim() : `Week ${i + 1}`,
    sets: (Array.isArray(w.sets) ? w.sets : []).filter(isObj).map(cleanSet),
  }));
}

/**
 * The weeks of a template, ready to store in a block. A copy, so a later
 * template change never rewrites a running block.
 * @param {string} templateId '531' | '531-bbb' | 'gzclp-t1' | 'smolov-jr' |
 *   'linear' | 'double'; anything else (incl. 'custom') gives []
 * @param {{sets?: number, reps?: number, repMin?: number, repMax?: number,
 *   startWeight?: number}} [opts] only for 'linear' (default 3x5) and
 *   'double' (default 3x8-12); startWeight fixes the first session's load
 * @returns {Array<{label:string, sets:Array<{pct?, add?, weight?, reps, repMin?, amrap?, rir?}>}>}
 *   double sets carry reps = repMax plus repMin
 */
export function materializeProgram(templateId, opts = {}) {
  const t = templateById(templateId);
  if (!t) return [];
  const o = isObj(opts) ? opts : {};
  const start = isNum(o.startWeight) && o.startWeight > 0 ? o.startWeight : null;
  const withStart = s => (start === null ? s : { ...s, weight: start });
  if (t.rule === 'linear') {
    const n = posInt(o.sets) || t.weeks[0].sets.length;
    const reps = posInt(o.reps) || t.weeks[0].sets[0].reps;
    return [{ label: `${n}×${reps}`, sets: Array.from({ length: n }, () => withStart({ reps })) }];
  }
  if (t.rule === 'double') {
    const n = posInt(o.sets) || t.weeks[0].sets.length;
    const repMax = posInt(o.repMax) || t.weeks[0].sets[0].reps;
    const repMin = Math.min(posInt(o.repMin) || t.weeks[0].sets[0].repMin, repMax);
    return [{
      label: `${n}×${repMin}–${repMax}`,
      sets: Array.from({ length: n }, () => withStart({ reps: repMax, repMin })),
    }];
  }
  return cleanWeeks(t.weeks);
}

function isLowerBody(name) {
  return typeof name === 'string' && LOWER_BODY_RE.test(name);
}

function tmIncrementOf(block) {
  if (isNum(block.tmIncrement) && block.tmIncrement >= 0) return block.tmIncrement;
  const t = templateById(block.template);
  const base = t ? t.defaultTmIncrement : DEFAULT_STEP;
  return isLowerBody(block.exercise) ? base * 2 : base;
}

function dayIds(block) {
  return Array.isArray(block.planDayIds) ? block.planDayIds.filter(id => typeof id === 'string' && id) : [];
}

/* The week a block starts at (0-based), within one pass of its weeks. */
function startWeekOf(block, len) {
  const w = posInt(block.startWeek);
  return w && len > 0 ? w % len : 0;
}

/**
 * A new program block per the contract (docs/fitness-data-model.md programs).
 * @param {object} fields { exercise, template, startDate, name?, endDate?,
 *   trainingMax?, roundTo?, tmIncrement?, advance?, weeks?, note?, by?,
 *   planDayIds?, startWeek?, plus materializeProgram opts (sets, reps, repMin,
 *   repMax, startWeight) }. weeks given (custom) are cleaned; otherwise
 *   materialised. startWeek (0-based) starts the block at that week, e.g. a
 *   new block that changes the training max mid-block.
 * @param {string} id the block id, e.g. uid('prog'); pure code can't mint one
 * @returns {{id, exercise, name, template, startDate, endDate, trainingMax,
 *   roundTo, tmIncrement, advance, weeks, note, by, planDayIds?, startWeek?}}
 *   template is 'custom' when unknown; trainingMax null for templates without
 *   one; tmIncrement defaults to the template's (doubled for lower body); by
 *   defaults to 'app'; planDayIds only when non-empty; startWeek only when
 *   > 0, taken modulo the number of weeks
 */
export function createProgramBlock(fields, id) {
  const f = isObj(fields) ? fields : {};
  const t = templateById(f.template);
  const exercise = typeof f.exercise === 'string' ? f.exercise.trim() : '';
  const block = {
    id: typeof id === 'string' && id ? id : (typeof f.id === 'string' && f.id ? f.id : null),
    exercise,
    name: typeof f.name === 'string' && f.name.trim() ? f.name.trim() : (t ? t.name : 'Custom'),
    template: t ? t.id : 'custom',
    startDate: dayNumber(f.startDate) !== null ? f.startDate : null,
    endDate: dayNumber(f.endDate) !== null ? f.endDate : null,
    trainingMax: isNum(f.trainingMax) && f.trainingMax > 0 && !(t && !t.usesTrainingMax) ? f.trainingMax : null,
    roundTo: stepOf(f.roundTo),
    tmIncrement: null,
    advance: f.advance === 'calendar' ? 'calendar' : 'session',
    weeks: Array.isArray(f.weeks) && f.weeks.length ? cleanWeeks(f.weeks) : materializeProgram(t ? t.id : null, f),
    note: typeof f.note === 'string' ? f.note.trim() : '',
    by: typeof f.by === 'string' && f.by ? f.by : 'app',
  };
  block.tmIncrement = tmIncrementOf({ ...block, tmIncrement: f.tmIncrement });
  const ids = dayIds(f);
  if (ids.length) block.planDayIds = ids;
  const sw = startWeekOf(f, block.weeks.length);
  if (sw) block.startWeek = sw;
  return block;
}

function coversDay(block, exercise, d) {
  if (!isObj(block) || block.exercise !== exercise) return false;
  const start = dayNumber(block.startDate);
  if (start === null || start > d) return false;
  const end = dayNumber(block.endDate);
  return end === null || d <= end;
}

/* Whether a block applies on a plan day. Ids that are all gone from the plan
 * (a plan reset makes new ones) no longer narrow anything. */
function inScope(block, planDayId, plan) {
  const ids = dayIds(block);
  if (!ids.length || planDayId === null) return true;
  if (plan && !ids.some(id => plan.some(day => isObj(day) && day.id === id))) return true;
  return ids.includes(planDayId);
}

/**
 * The program block that drives an exercise on a date: startDate <= date and
 * (endDate null or >= date); the latest startDate wins, a later array entry
 * wins a tie.
 * @param {Array} programs state.programs
 * @param {string} exercise exact exercise name as logged
 * @param {string} date YYYY-MM-DD
 * @param {{planDayId?: string, plan?: Array}} [opts] planDayId: skip blocks
 *   limited to other plan days; plan: lets stale planDayIds count as all days
 * @returns {object|null} the block itself (not a copy)
 */
export function activeProgram(programs, exercise, date, opts = {}) {
  const d = dayNumber(date);
  if (d === null || !Array.isArray(programs)) return null;
  const planDayId = opts && typeof opts.planDayId === 'string' ? opts.planDayId : null;
  const plan = opts && Array.isArray(opts.plan) ? opts.plan : null;
  let best = null;
  for (const b of programs) {
    if (!coversDay(b, exercise, d) || !inScope(b, planDayId, plan)) continue;
    if (!best || b.startDate >= best.startDate) best = b;
  }
  return best;
}

function scopesOverlap(a, b) {
  const ia = dayIds(a);
  const ib = dayIds(b);
  return !ia.length || !ib.length || ia.some(id => ib.includes(id));
}

/**
 * Switch an exercise to a new block without losing history: every block of
 * that exercise still running on newBlock.startDate (and sharing a plan day
 * with it) ends the day before; newBlock is appended, or replaces the entry
 * with its id. Nothing is deleted. A block that started on that same day ends
 * before it began and never matches again.
 * @param {Array} programs state.programs
 * @param {object} newBlock a block (createProgramBlock) with exercise and a
 *   valid startDate; otherwise the list comes back unchanged
 * @returns {Array} a new array; untouched blocks are the same objects
 */
export function switchProgram(programs, newBlock) {
  const list = Array.isArray(programs) ? [...programs] : [];
  const start = isObj(newBlock) ? dayNumber(newBlock.startDate) : null;
  if (start === null || typeof newBlock.exercise !== 'string' || !newBlock.exercise) return list;
  const hasId = typeof newBlock.id === 'string' && newBlock.id;
  const dayBefore = keyOf(start - 1);
  const out = list.map(b => {
    if (!isObj(b) || (hasId && b.id === newBlock.id)) return b;
    if (!coversDay(b, newBlock.exercise, start) || !scopesOverlap(b, newBlock)) return b;
    return { ...b, endDate: dayBefore };
  });
  const idx = hasId ? out.findIndex(b => isObj(b) && b.id === newBlock.id) : -1;
  if (idx >= 0) out[idx] = { ...newBlock };
  else out.push({ ...newBlock });
  return out;
}

/* Dates before `date` (and within the block) with a done set of the block's
 * exercise, on the block's plan days when ctx can tell. */
function blockDates(block, logs, date, ctx) {
  const start = dayNumber(block.startDate);
  const d = dayNumber(date);
  if (start === null || d === null) return [];
  const end = dayNumber(block.endDate);
  const plan = isObj(ctx) && Array.isArray(ctx.plan) ? ctx.plan : null;
  const ids = dayIds(block);
  const filter = plan && ids.length && ids.some(id => plan.some(day => isObj(day) && day.id === id));
  const out = [];
  for (const k of dateKeys(logs)) {
    const n = dayNumber(k);
    if (n < start || n >= d || (end !== null && n > end)) continue;
    if (!hasDoneSet(setsOf(logs, k, block.exercise))) continue;
    if (filter) {
      const pd = planDayFor(ctx, k);
      // Unknown plan day: count it rather than stall the program.
      if (pd && isObj(pd.day) && !ids.includes(pd.day.id)) continue;
    }
    out.push(k);
  }
  return out;
}

/**
 * Where a block stands on a date. 'session' advance: one step per earlier
 * session of the exercise (a date with a done set) since startDate; today's
 * own sets never count, so the target holds while training. 'calendar': one
 * step per Mon-Sun week since the week of startDate. Counting starts at the
 * block's startWeek (default 0).
 * @param {object} block a programs entry
 * @param {object} logs state.logs
 * @param {string} date YYYY-MM-DD
 * @param {object} [ctx] the state (plan, sessions, cycleAnchor), only needed
 *   for blocks with planDayIds
 * @returns {{weekIndex:number, cycle:number, sessionsDone:number}} all 0
 *   before startDate; cycle = completed passes through block.weeks
 */
export function programWeekIndex(block, logs, date, ctx) {
  const zero = { weekIndex: 0, cycle: 0, sessionsDone: 0 };
  if (!isObj(block)) return zero;
  const start = dayNumber(block.startDate);
  const d = dayNumber(date);
  if (start === null || d === null || d < start) return zero;
  const sessionsDone = blockDates(block, logs, date, ctx).length;
  const len = Array.isArray(block.weeks) && block.weeks.length ? block.weeks.length : 1;
  const step = startWeekOf(block, len) + (block.advance === 'calendar'
    ? Math.floor(((d - monIndex(d)) - (start - monIndex(start))) / 7)
    : sessionsDone);
  return { weekIndex: step % len, cycle: Math.floor(step / len), sessionsDone };
}

function normPct(p) {
  if (!isNum(p) || p <= 0) return null;
  return p <= 1.5 ? round2(p * 100) : p;
}

/* A stored set (program week or prescription) as a target row. */
function resolveSet(s, tm, step) {
  const pct = normPct(s.pct);
  let weight = null;
  if (isNum(s.weight) && s.weight >= 0) weight = s.weight;
  else if (pct !== null && tm !== null) weight = round2(roundLoad(tm * pct / 100, step) + (isNum(s.add) ? s.add : 0));
  return {
    weight,
    reps: isNum(s.reps) ? s.reps : null,
    amrap: s.amrap === true,
    pct,
    rir: isNum(s.rir) ? s.rir : null,
  };
}

function plainSet(s) {
  return { weight: s.weight, reps: s.reps, amrap: false, pct: null, rir: null };
}

/* Best capped e1RM over sessions in (from, to], day numbers. */
function bestE1rm(logs, name, from, to) {
  let best = null;
  for (const k of dateKeys(logs)) {
    const n = dayNumber(k);
    if (n <= from || n > to) continue;
    for (const s of completedSets(setsOf(logs, k, name))) {
      const e = epley(s.weight, s.reps);
      if (e !== null && (best === null || e > best)) best = e;
    }
  }
  return best;
}

/* linear / double blocks: the rule runs on this block's own sessions; the
 * first session uses the stored start weight, else ~2 RIR from e1RM. */
function ruleTargets(block, week, rule, logs, date, ctx, step) {
  const sets = (Array.isArray(week.sets) ? week.sets : []).filter(isObj);
  const first = sets[0] || {};
  const reps = posInt(first.reps) || (rule === 'double' ? 12 : 5);
  const pe = {
    sets: sets.length || 3,
    reps,
    repMin: rule === 'double' ? Math.min(posInt(first.repMin) || reps, reps) : undefined,
    repMax: rule === 'double' ? reps : undefined,
    progression: rule,
  };
  const assisted = isAssisted(block.exercise, isObj(ctx) ? ctx.exerciseMeta : null);
  const prev = blockDates(block, logs, date, ctx).map(k => ({ date: k, sets: setsOf(logs, k, block.exercise) }));
  const hasStart = sets.length && sets.every(s => isNum(s.weight) && s.weight > 0);
  const usable = prev.some(p => completedSets(p.sets).length);
  if (!usable && hasStart) {
    return { sets: sets.map(s => resolveSet({ ...s, reps: rule === 'double' ? pe.repMin : s.reps }, null, step)), note: null, basedOn: null };
  }
  const d = dayNumber(date);
  const nt = nextTarget(pe, prev, {
    increment: tmIncrementOf(block),
    roundTo: step,
    assisted,
    e1rm: usable ? null : bestE1rm(logs, block.exercise, d - 1 - E1RM_DAYS, d - 1),
  });
  if (!nt) return { sets: sets.map(s => resolveSet({ reps: rule === 'double' ? pe.repMin : s.reps }, null, step)), note: null, basedOn: null };
  return { sets: nt.sets.map(plainSet), note: nt.reason, basedOn: nt.basedOn };
}

/**
 * The sets a block prescribes on a date.
 * @param {object} block a programs entry
 * @param {object} logs state.logs
 * @param {string} date YYYY-MM-DD
 * @param {object} [ctx] the state (plan, sessions, cycleAnchor, exerciseMeta)
 * @returns {{label:string, weekLabel:string, weekIndex:number, cycle:number,
 *   weekCount:number, sessionsDone:number, trainingMax:number|null,
 *   sets:Array<{weight:number|null, reps:number|null, amrap:boolean,
 *   pct:number|null, rir:number|null}>, note:string|null, basedOn:string|null}|null}
 *   trainingMax is the effective one (+ tmIncrement per completed cycle);
 *   pct loads round to block.roundTo and then add `add`; weight null when it
 *   can't be computed (no training max, or a linear / double block with no
 *   history). For linear / double blocks note and basedOn explain the step.
 *   null for a non-object block.
 */
export function programTargets(block, logs, date, ctx) {
  if (!isObj(block)) return null;
  const idx = programWeekIndex(block, logs, date, ctx);
  const weeks = Array.isArray(block.weeks) ? block.weeks : [];
  const week = isObj(weeks[idx.weekIndex]) ? weeks[idx.weekIndex] : { label: '', sets: [] };
  const step = stepOf(block.roundTo);
  const t = templateById(block.template);
  const rule = t ? t.rule : null;
  const tm = !rule && isNum(block.trainingMax) && block.trainingMax > 0
    ? round2(block.trainingMax + idx.cycle * tmIncrementOf(block))
    : null;
  let sets;
  let note = null;
  let basedOn = null;
  if (rule) {
    ({ sets, note, basedOn } = ruleTargets(block, week, rule, logs, date, ctx, step));
  } else {
    sets = (Array.isArray(week.sets) ? week.sets : []).filter(isObj).map(s => resolveSet(s, tm, step));
  }
  return {
    label: typeof block.name === 'string' && block.name ? block.name : (t ? t.name : 'Program'),
    weekLabel: typeof week.label === 'string' && week.label ? week.label : `Week ${idx.weekIndex + 1}`,
    weekIndex: idx.weekIndex,
    cycle: idx.cycle,
    weekCount: weeks.length,
    sessionsDone: idx.sessionsDone,
    trainingMax: tm,
    sets,
    note,
    basedOn,
  };
}

function progressionRule(pe) {
  if (RULES.includes(pe.progression)) return pe.progression;
  return posInt(pe.repMin) && posInt(pe.repMax) ? 'double' : 'linear';
}

/* Earlier sessions with counted sets, newest first. */
function usableSessions(sessions, assisted) {
  const out = [];
  for (const s of Array.isArray(sessions) ? sessions : []) {
    if (!isObj(s) || dayNumber(s.date) === null) continue;
    const sets = completedSets(s.sets);
    if (!sets.length) continue;
    out.push({ date: s.date, sets, top: topWeight(sets, assisted), e1: assisted ? null : rawE1rm(sets) });
  }
  return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

function backOff(w, inc, assisted) {
  const k = Math.max(1, Math.round((w * BACK_OFF_PCT) / inc));
  return assisted ? round2(w + k * inc) : Math.max(0, round2(w - k * inc));
}

/**
 * The next session's sets from the plan exercise's progression rule.
 * double (rep range): when at least `sets` sets at the top weight W reached
 * repMax, W + increment at repMin; otherwise W again with +1 rep on each set
 * under repMax (a set not done at W aims for repMin). linear (fixed reps):
 * all sets at W hit reps => W + increment; 3 missed sessions in a row at W =>
 * about -10% in whole increments; else repeat. A latest session whose best
 * e1RM is under 80% of the median of the last 5 (needs 3+) is skipped as
 * unusual (another gym), and so is the one before it if it is too.
 * Assisted: the increment comes off the counterweight.
 * @param {object} planExercise { sets, reps, repMin?, repMax?, increment?, progression? }
 * @param {Array<{date:string, sets:Array}>} sessions earlier sessions of the
 *   exercise (raw logged sets, any order)
 * @param {{increment?: number, roundTo?: number, assisted?: boolean, e1rm?: number}} [opts]
 *   increment: kg (else planExercise.increment, else 2.5); e1rm: with no
 *   usable session, start at ~2 reps in reserve from it (rounded down)
 * @returns {null|{rule:'double'|'linear', action:'add-load'|'add-reps'|'repeat'|'back-off'|'start',
 *   weight:number, sets:Array<{weight:number, reps:number}>, reason:string,
 *   basedOn:string|null, skipped:string[]}} null for 'manual' or without a basis
 */
export function nextTarget(planExercise, sessions, opts = {}) {
  const pe = isObj(planExercise) ? planExercise : {};
  const o = isObj(opts) ? opts : {};
  const rule = progressionRule(pe);
  if (rule === 'manual') return null;
  const inc = isNum(o.increment) && o.increment > 0 ? o.increment
    : (isNum(pe.increment) && pe.increment > 0 ? pe.increment : DEFAULT_STEP);
  const assisted = o.assisted === true;
  let repMin = posInt(pe.repMin);
  let repMax = posInt(pe.repMax);
  if (!repMin || !repMax) {
    repMin = posInt(pe.reps) || repMin || repMax;
    repMax = repMin;
  }
  if (repMin && repMax && repMin > repMax) [repMin, repMax] = [repMax, repMin];

  const hist = usableSessions(sessions, assisted);
  const skipped = [];
  let i = 0;
  const recent = hist.slice(0, 5).map(h => h.e1);
  if (!assisted && recent.length >= 3) {
    const med = median(recent);
    while (i < hist.length && hist[i].e1 < med * UNUSUAL_PCT) { skipped.push(hist[i].date); i += 1; }
  }
  const rest = hist.slice(i);
  const basis = rest[0];
  const n = posInt(pe.sets) || (basis ? basis.sets.length : 3);
  const make = (action, weight, sets, reason) => ({
    rule, action, weight, sets, reason, basedOn: basis ? basis.date : null, skipped,
  });

  if (!basis) {
    const reps = rule === 'double' ? repMin : (posInt(pe.reps) || repMax);
    if (assisted || !isNum(o.e1rm) || o.e1rm <= 0 || !reps) return null;
    const w = roundLoad(o.e1rm / (1 + (reps + 2) / 30), stepOf(o.roundTo), 'down');
    return make('start', w, Array.from({ length: n }, () => ({ weight: w, reps })),
      `start: ~2 reps in reserve from e1RM ${fmt(o.e1rm)}`);
  }

  const W = basis.top;
  const up = assisted ? Math.max(0, round2(W - inc)) : round2(W + inc);
  const atW = basis.sets.filter(s => s.weight === W);
  if (rule === 'double') {
    const lo = repMin || Math.min(...atW.map(s => s.reps));
    const hi = repMax || lo;
    if (atW.filter(s => s.reps >= hi).length >= n) {
      return make('add-load', up, Array.from({ length: n }, () => ({ weight: up, reps: lo })), `all sets hit ${fmt(hi)}`);
    }
    const sets = Array.from({ length: n }, (_, k) => ({
      weight: W,
      reps: atW[k] ? Math.min(hi, atW[k].reps + 1) : lo,
    }));
    return make('add-reps', W, sets, `+1 rep on sets under ${fmt(hi)}`);
  }

  const reps = posInt(pe.reps) || repMax || Math.max(...atW.map(s => s.reps));
  const hitAll = h => h.sets.filter(s => s.weight === h.top && s.reps >= reps).length >= n;
  const same = w => Array.from({ length: n }, () => ({ weight: w, reps }));
  if (hitAll(basis)) return make('add-load', up, same(up), `all sets hit ${fmt(reps)}`);
  let fails = 0;
  for (const h of rest) {
    if (h.top !== W || hitAll(h)) break;
    fails += 1;
  }
  if (fails >= FAILS_BEFORE_BACK_OFF) {
    const w = backOff(W, inc, assisted);
    return make('back-off', w, same(w), `missed ${fmt(reps)} reps ${fails} sessions in a row: back off ~10%`);
  }
  return make('repeat', W, same(W), `missed ${fmt(reps)} reps: repeat (${fails} of ${FAILS_BEFORE_BACK_OFF})`);
}

/**
 * The training max to start a block with: pct (default 90) of the best
 * capped e1RM in the `days` (default 60) days up to and including date,
 * rounded down to roundTo. Use the template's tmPct (Smolov Jr: 100).
 * @param {object} logs state.logs
 * @param {string} name exercise name
 * @param {string} date YYYY-MM-DD
 * @param {{pct?: number, days?: number, roundTo?: number, assisted?: boolean,
 *   exerciseMeta?: object}} [opts]
 * @returns {number|null} null without an e1RM in the window or for assisted
 */
export function suggestTrainingMax(logs, name, date, opts = {}) {
  const o = isObj(opts) ? opts : {};
  const d = dayNumber(date);
  if (d === null || resolveAssisted(name, o)) return null;
  const days = posInt(o.days) || E1RM_DAYS;
  const pct = normPct(o.pct) || 90;
  const best = bestE1rm(logs, name, d - days, d);
  return best === null ? null : roundLoad(best * pct / 100, stepOf(o.roundTo), 'down');
}

function planDayIdOf(state, date, pe) {
  const plan = Array.isArray(state.plan) ? state.plan : [];
  if (pe && pe.id) {
    const day = plan.find(p => isObj(p) && Array.isArray(p.exercises) && p.exercises.some(e => isObj(e) && e.id === pe.id));
    if (day && typeof day.id === 'string') return day.id;
  }
  const pd = planDayFor(state, date);
  return pd && isObj(pd.day) && typeof pd.day.id === 'string' ? pd.day.id : null;
}

/* Earlier sessions with counted sets, newest first; the same plan day's
 * when there are any (bench differs between Push A and Push B), else all. */
function previousSessions(state, logs, date, name, planDayId) {
  const d = dayNumber(date);
  const all = [];
  for (const k of dateKeys(logs)) {
    if (dayNumber(k) >= d) continue;
    const sets = setsOf(logs, k, name);
    if (completedSets(sets).length) all.push({ date: k, sets });
  }
  all.reverse();
  if (planDayId === null) return all;
  const same = all.filter(s => {
    const pd = planDayFor(state, s.date);
    return pd && isObj(pd.day) && pd.day.id === planDayId;
  });
  return same.length ? same : all;
}

/* The last date before `date` a block in this day's scope covered: the plan
 * rule must not read 5/3/1 singles as a failed 3x10. */
function lastBlockDay(programs, name, date, planDayId, plan) {
  const d = dayNumber(date);
  let last = null;
  for (const b of Array.isArray(programs) ? programs : []) {
    if (!isObj(b) || b.exercise !== name || !inScope(b, planDayId, plan)) continue;
    const start = dayNumber(b.startDate);
    if (start === null || start >= d) continue;
    const end = dayNumber(b.endDate);
    const covered = end === null ? d - 1 : Math.min(end, d - 1);
    if (covered >= start && (last === null || covered > last)) last = covered;
  }
  return last;
}

function prescriptionFor(state, date, name) {
  const day = isObj(state.prescriptions) ? state.prescriptions[date] : null;
  const rx = isObj(day) ? day[name] : null;
  if (!isObj(rx) || !Array.isArray(rx.sets)) return null;
  const sets = rx.sets.filter(isObj);
  if (!sets.length) return null;
  return { sets, note: typeof rx.note === 'string' && rx.note.trim() ? rx.note.trim() : null, by: rx.by };
}

/**
 * Today's target for one exercise, following the contract precedence:
 * 1 prescriptions[date][exerciseName] (pct sets use the active block's
 * training max), 2 the active program block (scoped to the plan day of
 * planExercise), 3 the plan exercise's progression rule ('manual' gives
 * none) over earlier sessions of the same plan day, after the last program
 * block for it ended (none left: start ~2 RIR from the 60-day e1RM),
 * 4 last session's numbers (same plan day preferred).
 * @param {object} state the fitness state (logs, plan, sessions, cycleAnchor,
 *   programs, prescriptions, exerciseMeta)
 * @param {string} date YYYY-MM-DD
 * @param {string} exerciseName the shown (possibly swapped) name
 * @param {object} [planExercise] plan[d].exercises[e]; omit for ad-hoc work
 * @returns {{source:'prescription'|'program'|'progression'|'last'|null,
 *   label:string|null, sets:Array<{weight:number|null, reps:number|null,
 *   amrap:boolean, pct:number|null, rir:number|null}>, note:string|null,
 *   basedOn:string|null, blockId:string|null}} label: 'Claude' | 'Me' |
 *   '<block> · <week>' | 'Auto' | 'Last time'; basedOn: the session date the
 *   numbers come from; sets [] and nulls when nothing applies
 */
export function targetsFor(state, date, exerciseName, planExercise) {
  const none = { source: null, label: null, sets: [], note: null, basedOn: null, blockId: null };
  const st = isObj(state) ? state : {};
  const d = dayNumber(date);
  if (d === null || typeof exerciseName !== 'string' || !exerciseName) return none;
  // Deload sessions never count: not for progression, last time or program weeks.
  const logs = trainingLogs(st, null);
  const plan = Array.isArray(st.plan) ? st.plan : [];
  const pe = isObj(planExercise) ? planExercise : null;
  const planDayId = planDayIdOf(st, date, pe);
  const deload = deloadOn(st, date);
  // In a deload the target gets lighter (a program's own deload week stays as it is).
  const withDeload = (res, step) => (deload && res.sets.length
    ? { ...res, label: `Deload · ${res.label}`, sets: deloadSets(res.sets, deload.style, step), deload: deload.style }
    : res);
  const block = activeProgram(st.programs, exerciseName, date, { planDayId, plan });
  const pt = block ? programTargets(block, logs, date, st) : null;

  const rx = prescriptionFor(st, date, exerciseName);
  if (rx) {
    const step = block ? stepOf(block.roundTo) : DEFAULT_STEP;
    return {
      source: 'prescription',
      label: rx.by === 'claude' ? 'Claude' : 'Me',
      sets: rx.sets.map(s => resolveSet(s, pt ? pt.trainingMax : null, step)),
      note: rx.note,
      basedOn: null,
      blockId: null,
    };
  }
  if (pt && pt.sets.length) {
    const blockNote = typeof block.note === 'string' && block.note.trim() ? block.note.trim() : null;
    const res = {
      source: 'program',
      label: `${pt.label} · ${pt.weekLabel}`,
      sets: pt.sets,
      note: pt.note || blockNote,
      basedOn: pt.basedOn,
      blockId: typeof block.id === 'string' ? block.id : null,
    };
    return /deload/i.test(pt.weekLabel) ? res : withDeload(res, stepOf(block.roundTo));
  }

  const history = previousSessions(st, logs, date, exerciseName, planDayId);
  if (pe && progressionRule(pe) !== 'manual') {
    const assisted = isAssisted(exerciseName, st.exerciseMeta);
    const cut = lastBlockDay(st.programs, exerciseName, date, planDayId, plan);
    const own = cut === null ? history : history.filter(s => dayNumber(s.date) > cut);
    const inc = isNum(pe.increment) && pe.increment > 0
      ? pe.increment
      : learnIncrement(logs, exerciseName, DEFAULT_STEP, { assisted });
    const nt = nextTarget(pe, own, {
      increment: inc,
      assisted,
      e1rm: own.length ? null : bestE1rm(logs, exerciseName, d - 1 - E1RM_DAYS, d - 1),
    });
    if (nt) {
      const note = nt.skipped.length
        ? `${nt.reason} (skipped ${nt.skipped.join(', ')}: looked unusual)`
        : nt.reason;
      return withDeload({ source: 'progression', label: 'Auto', sets: nt.sets.map(plainSet), note, basedOn: nt.basedOn, blockId: null }, inc);
    }
  }
  const last = history[0];
  if (last) {
    return withDeload({
      source: 'last',
      label: 'Last time',
      sets: completedSets(last.sets).map(plainSet),
      note: null,
      basedOn: last.date,
      blockId: null,
    }, DEFAULT_STEP);
  }
  return none;
}

